/**
 * @fileoverview Centralized logging utility for consistent application logging.
 *
 * This module provides a configurable logger that can be used across the application
 * for consistent log formatting and centralized control of log levels.
 */

//#region Types

/**
 * Available log levels
 */
export enum LogLevel {
	Error = "error",
	Warn = "warn",
	Info = "info",
	Debug = "debug",
}

/**
 * Logger instance configuration options
 */
export interface LoggerOptions {
	/** Component or file name for context */
	context: string;
	/** Whether to include timestamps in log messages */
	includeTimestamp?: boolean;
	/** Minimum log level to display (lower levels will be hidden) */
	minLevel?: LogLevel;
}

/**
 * Logger interface with methods for different log levels
 */
export interface Logger {
	/** Log an error message with optional context */
	error(methodName: string, error: unknown, additionalContext?: string): void;

	/** Log a warning message */
	warn(methodName: string, message: string, additionalInfo?: unknown): void;

	/** Log an informational message */
	info(methodName: string, message: string, additionalInfo?: unknown): void;

	/** Log a debug message */
	debug(methodName: string, message: string, additionalInfo?: unknown): void;

	/** Log a normal message */
	log(methodName: string, message: string, additionalInfo?: unknown): void;
}

//#endregion

//#region Implementation

/**
 * Creates a logger instance for a specific component or context
 *
 * @param options - Logger configuration options
 * @returns A configured logger instance
 *
 * @example
 * ```typescript
 * // Create a logger for TranscriptModal Component
 * private logger = createLogger({ context: 'transcript_modal.ts' });
 *
 * // Log an error w/ no additional context
 * this.logger.error('submitTranscript', error);
 *
 * // Log an error with additional context
 * this.logger.error('submitTranscript', error, 'Failed while sending data');
 * ```
 */
export function createLogger(options: LoggerOptions): Logger {
	const { context, includeTimestamp = false, minLevel = LogLevel.Info } = options;

	/**
	 * Get timestamp string if enabled
	 */
	const getTimestamp = (): string => {
		if (!includeTimestamp) return "";
		return `[${new Date().toLocaleString()}] `;
	};

	/**
	 * Format a log message with consistent structure
	 */
	const formatMessage = (methodName: string, additionalContext?: string): string => {
		const contextInfo = additionalContext ? ` - ${additionalContext}` : "";
		return `[${context}] Error in ${methodName}${contextInfo}:`;
	};

	/**
	 * Check if a log level should be displayed based on minLevel setting
	 */
	const shouldLog = (level: LogLevel): boolean => {
		const levels = Object.values(LogLevel);
		const minLevelIndex = levels.indexOf(minLevel);
		const currentLevelIndex = levels.indexOf(level);

		return currentLevelIndex <= minLevelIndex;
	};

	return {
		/**
		 * Log an error message with optional additional context
		 * @param methodName - Name of the method where the error occurred
		 * @param error - The error object or message to log
		 * @param additionalContext - Optional additional context for the error
		 */
		error(methodName: string, error: unknown, additionalContext?: string): void {
			if (!shouldLog(LogLevel.Error)) return;
			console.error(`${getTimestamp()}${formatMessage(methodName, additionalContext)}\n`, error);
		},

		/**
		 * Log a warning message
		 * @param methodName - Name of the method where the warning occurred
		 * @param message - Warning message to log
		 * @param additionalInfo - Optional additional information to include in the log
		 */
		warn(methodName: string, message: string, additionalInfo?: unknown): void {
			if (!shouldLog(LogLevel.Warn)) return;
			const args: unknown[] = [`${getTimestamp()}[${context}] ${methodName}: ${message}`];
			if (additionalInfo !== undefined) args.push(additionalInfo);
			console.warn(...args);
		},

		/**
		 * Log an informational message
		 * @param methodName - Name of the method where the info occurred
		 * @param message - Informational message to log
		 * @param additionalInfo - Optional additional information to include in the log
		 */
		info(methodName: string, message: string, additionalInfo?: unknown): void {
			if (!shouldLog(LogLevel.Info)) return;
			const args: unknown[] = [`${getTimestamp()}[${context}] ${methodName}: ${message}`];
			if (additionalInfo !== undefined) args.push(additionalInfo);
			console.info(...args);
		},

		/**
		 * Log a debug message
		 * @param methodName - Name of the method where the debug occurred
		 * @param message - Debug message to log
		 * @param additionalInfo - Optional additional information to include in the log
		 */
		debug(methodName: string, message: string, additionalInfo?: unknown): void {
			if (!shouldLog(LogLevel.Debug)) return;
			const args: unknown[] = [`${getTimestamp()}[${context}] ${methodName}: ${message}`];
			if (additionalInfo !== undefined) args.push(additionalInfo);
			console.debug(...args);
		},

		/**
		 * Log a normal message without any specific log level
		 * @param methodName - Name of the method where the log occurred
		 * @param message - Message to log
		 * @param additionalInfo - Optional additional information to include in the log
		 */
		log(methodName: string, message: string, additionalInfo?: unknown): void {
			const args: unknown[] = [`${getTimestamp()}[${context}] ${methodName}: ${message}`];
			if (additionalInfo !== undefined) args.push(additionalInfo);
			console.log(...args);
		},
	};
}

//#endregion
